import { getAuth } from 'firebase/auth';

const FUNCTIONS_BASE = import.meta.env.VITE_FUNCTIONS_BASE as string;
const TOKENS_PER_GENERATION = 20;

/**
 * Fetch current token count for the authenticated user.
 */
export async function getTokenCount(): Promise<number> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required');
  const token = await user.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE}/getTokenCount`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return typeof data.tokens === 'number' ? data.tokens : 0;
}

export { TOKENS_PER_GENERATION };

export interface GenerateTryOnResult {
  image: string; // data URL
  tokensRemaining: number;
}

/**
 * Run virtual try-on: garment + photo (data URLs) → result image.
 * useLegacyModel: true = tryOn (Gemini), false = tryOnFal (FAL with preferFidelity).
 */
export async function generateTryOn(
  garmentDataUrl: string,
  photoDataUrl: string,
  useLegacyModel: boolean
): Promise<GenerateTryOnResult> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in required');
  const idToken = await user.getIdToken();

  const url = useLegacyModel
    ? `${FUNCTIONS_BASE}/tryOn`
    : `${FUNCTIONS_BASE}/tryOnFal`;

  const body: Record<string, unknown> = {
    garment: garmentDataUrl,
    photo: photoDataUrl,
  };
  if (!useLegacyModel) {
    body.preferFidelity = true;
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  if (!data.image || typeof data.image !== 'string') {
    throw new Error(data.error || 'No image in response');
  }
  return {
    image: data.image,
    tokensRemaining: typeof data.tokensRemaining === 'number' ? data.tokensRemaining : 0,
  };
}
